/**
 * Data layer for `landing-pages`.
 *
 * Thin typed fetch wrappers over `/api/v1/landing-pages`. The BE surface is
 * the generic CRUD-idempotent shape: GET list, POST create, DELETE by id.
 * Every request carries `x-tenant-id: t1` for this MVP dash.
 *
 * Per the dash contract, raw `fetch()` is confined to this file.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Shared types + schemas
// ---------------------------------------------------------------------------

export const LANDING_PAGE_STATUSES = ["draft", "active", "paused"] as const;
export type LandingPageStatus = (typeof LANDING_PAGE_STATUSES)[number];

/** Server row. Unknown extra fields are tolerated. */
export interface LandingPageRow {
	id: string;
	tenant_id: string;
	name: string;
	url: string;
	campaign: string | null;
	status: LandingPageStatus | null;
	preview_url: string | null;
	cvr: number | null;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListLandingPagesResponse {
	count: number;
	items: LandingPageRow[];
}

/** Client-side zod schema powering the Create drawer. */
export const createLandingPageSchema = z.object({
	name: z
		.string()
		.trim()
		.min(2, "Name must be at least 2 characters.")
		.max(120, "Name must be 120 characters or fewer."),
	url: z
		.string()
		.trim()
		.url("Enter a full URL, e.g. https://example.com/offer."),
	campaign: z
		.string()
		.trim()
		.max(80, "Campaign must be 80 characters or fewer.")
		.optional()
		.or(z.literal("")),
	preview_url: z
		.string()
		.trim()
		.url("Preview must be a full URL.")
		.optional()
		.or(z.literal("")),
});

export type CreateLandingPageInput = z.infer<typeof createLandingPageSchema>;

/** Payload actually sent to the API (empty strings collapsed to null). */
export interface CreateLandingPageBody {
	name: string;
	url: string;
	campaign?: string | null;
	preview_url?: string | null;
}

/** Collapse empty strings → null / strip, ready to POST. */
export function normalizeCreate(
	input: CreateLandingPageInput,
): CreateLandingPageBody {
	const body: CreateLandingPageBody = {
		name: input.name.trim(),
		url: input.url.trim(),
	};
	const campaign = input.campaign?.trim();
	if (campaign) body.campaign = campaign;
	const preview = input.preview_url?.trim();
	if (preview) body.preview_url = preview;
	return body;
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

const BASE = "/api/v1/landing-pages";
const TENANT = "t1";

function tenantHeaders(extra: Record<string, string> = {}): HeadersInit {
	return {
		accept: "application/json",
		"x-tenant-id": TENANT,
		...extra,
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLandingPagesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: tenantHeaders(),
	});
	return parseOrThrow<ListLandingPagesResponse>(res);
}

export async function create(
	input: CreateLandingPageInput,
): Promise<LandingPageRow> {
	const body = normalizeCreate(input);
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: tenantHeaders({ "content-type": "application/json" }),
		body: JSON.stringify(body),
	});
	return parseOrThrow<LandingPageRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: tenantHeaders(),
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const landingPagesQueryKeys = {
	all: ["landing-pages"] as const,
	list: () => [...landingPagesQueryKeys.all] as const,
	detail: (id: string) => [...landingPagesQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

export function formatCvr(cvr: number | null | undefined): string {
	if (cvr == null || Number.isNaN(cvr)) return "—";
	const n = Number(cvr);
	if (!Number.isFinite(n) || n < 0) return "—";
	// Accept either 0–1 fraction or already-scaled percent.
	const pct = n > 1 ? n : n * 100;
	return `${pct.toFixed(2)}%`;
}

export function formatStatusLabel(
	status: LandingPageStatus | null | undefined,
): string {
	if (!status) return "Draft";
	return status.charAt(0).toUpperCase() + status.slice(1);
}
